const PSTL = (() => {
  const H_UP = 0.03, H_UP_STYLE = 0.03, H_DOWN = 1.0;   // mm: logos/text sit practically FLUSH (0.03 mm proud) and plate styles (line, number backer) 0.02 mm proud, so the Fill tool can pick each one; both go 1 mm deep
  const b64 = s => { const bin = atob(s), u = new Uint8Array(bin.length); for (let i = 0; i < bin.length; i++) u[i] = bin.charCodeAt(i); return u; };
  async function inflate(u8) {
    const ds = new DecompressionStream('deflate'); const w = ds.writable.getWriter(); w.write(u8); w.close();
    return new Uint8Array(await new Response(ds.readable).arrayBuffer());
  }
  async function decode(d) {
    const rd = async (s, scale, base) => {
      const u = await inflate(b64(s)); const q = new Int16Array(u.buffer, u.byteOffset, u.byteLength / 2);
      const out = new Float32Array(d.nT * d.nS);
      for (let r = 0; r < d.nT; r++) { let acc = 0; for (let c = 0; c < d.nS; c++) { acc += q[r * d.nS + c]; out[r * d.nS + c] = base + acc * scale; } }
      return out;
    };
    const Y = await rd(d.y, 0.01, d.yc), X = await rd(d.x, 0.01, d.X0);
    const mb = await inflate(b64(d.mask)); const M = new Uint8Array(d.mT * d.mS);
    for (let k = 0; k < M.length; k++) M[k] = (mb[k >> 3] >> (7 - (k & 7))) & 1;
    return { d, X, Y, M };
  }
  // 2x finer working grid for the logos (0.25 mm cells instead of 0.5 mm): sharper, smoother edges. The surface itself is unchanged.
  function refine(P, f) {
    const d = P.d, W = d.mS, H = d.mT, W2 = (W - 1) * f + 1, H2 = (H - 1) * f + 1, M2 = new Uint8Array(W2 * H2);
    for (let r = 0; r < H2; r++) { const fr = r / f, r0 = Math.min(H - 2, Math.floor(fr)), wr = fr - r0;
      for (let c = 0; c < W2; c++) { const fc = c / f, c0 = Math.min(W - 2, Math.floor(fc)), wc = fc - c0, k = r0 * W + c0;
        const v = (P.M[k] * (1 - wc) + P.M[k + 1] * wc) * (1 - wr) + (P.M[k + W] * (1 - wc) + P.M[k + W + 1] * wc) * wr;
        M2[r * W2 + c] = v > 0.5 ? 1 : 0; } }
    return { d: Object.assign({}, d, { mS: W2, mT: H2, mDS: d.mDS / f }), X: P.X, Y: P.Y, M: M2 };
  }
  // bilinear surface lookup in developed coordinates: S (mm across the curve), T (mm up)
  function surf(P, S, T) {
    const d = P.d;
    let gi = (S - d.S0) / d.DS, gj = T / d.DS;
    gi = Math.max(0, Math.min(d.nS - 1.001, gi)); gj = Math.max(0, Math.min(d.nT - 1.001, gj));
    const i0 = Math.floor(gi), j0 = Math.floor(gj), fi = gi - i0, fj = gj - j0;
    const k = j0 * d.nS + i0, n = d.nS;
    const lerp = A => (A[k] * (1 - fi) + A[k + 1] * fi) * (1 - fj) + (A[k + n] * (1 - fi) + A[k + n + 1] * fi) * fj;
    return [lerp(P.X), lerp(P.Y), d.Z0 + T];
  }
  function pointAt(P, S, T, h) {
    const c = surf(P, S, T), a = surf(P, S + 1, T), b = surf(P, S - 1, T), u = surf(P, S, T + 1), v = surf(P, S, T - 1);
    const t1 = [a[0] - b[0], a[1] - b[1], a[2] - b[2]], t2 = [u[0] - v[0], u[1] - v[1], u[2] - v[2]];
    let n = [t1[1] * t2[2] - t1[2] * t2[1], t1[2] * t2[0] - t1[0] * t2[2], t1[0] * t2[1] - t1[1] * t2[0]];
    const L = Math.hypot(n[0], n[1], n[2]) || 1; n = [n[0] / L, n[1] / L, n[2] / L];
    if (n[0] > 0) n = [-n[0], -n[1], -n[2]];          // front of the plate faces -X
    return [c[0] + n[0] * h, c[1] + n[1] * h, c[2] + n[2] * h];
  }
  // F: coverage field (0..1) on a grid nx*ny, row r <-> T = r*cell, col c <-> S = S0 + c*cell.
  function meshFromField(P, F, nx, ny, cell, S0, thr) {
    const vS = [], vT = [], tris = [], segs = [];
    const nodeId = new Int32Array(nx * ny).fill(-1), hId = new Int32Array(nx * ny).fill(-1), vId = new Int32Array(nx * ny).fill(-1);
    const addV = (s, t) => { vS.push(s); vT.push(t); return vS.length - 1; };
    const inside = (r, c) => F[r * nx + c] > thr;
    const node = (r, c) => nodeId[r * nx + c] >= 0 ? nodeId[r * nx + c] : (nodeId[r * nx + c] = addV(S0 + c * cell, r * cell));
    const clampT = t => Math.max(0.03, Math.min(0.97, t));
    const hEdge = (r, c) => { // between (r,c) and (r,c+1)
      const k = r * nx + c; if (hId[k] >= 0) return hId[k];
      const a = F[k], b = F[k + 1], t = clampT((thr - a) / (b - a));
      return (hId[k] = addV(S0 + (c + t) * cell, r * cell));
    };
    const vEdge = (r, c) => { // between (r,c) and (r+1,c)
      const k = r * nx + c; if (vId[k] >= 0) return vId[k];
      const a = F[k], b = F[k + nx], t = clampT((thr - a) / (b - a));
      return (vId[k] = addV(S0 + c * cell, (r + t) * cell));
    };
    for (let r = 0; r < ny - 1; r++) for (let c = 0; c < nx - 1; c++) {
      const i0 = inside(r, c), i1 = inside(r, c + 1), i2 = inside(r + 1, c + 1), i3 = inside(r + 1, c);
      const cnt = i0 + i1 + i2 + i3; if (!cnt) continue;
      if (cnt === 4) { const a = node(r, c), b = node(r, c + 1), d = node(r + 1, c + 1), e = node(r + 1, c); tris.push([a, b, d], [a, d, e]); continue; }
      // build polygon walking CCW: corners 0(r,c) 1(r,c+1) 2(r+1,c+1) 3(r+1,c)
      const cin = [i0, i1, i2, i3], poly = [], isX = [];
      const corner = k => k === 0 ? node(r, c) : k === 1 ? node(r, c + 1) : k === 2 ? node(r + 1, c + 1) : node(r + 1, c);
      const edge = k => k === 0 ? hEdge(r, c) : k === 1 ? vEdge(r, c + 1) : k === 2 ? hEdge(r + 1, c) : vEdge(r, c);
      const saddle = cnt === 2 && i0 === i2;
      const centerIn = saddle && (F[r * nx + c] + F[r * nx + c + 1] + F[(r + 1) * nx + c + 1] + F[(r + 1) * nx + c]) / 4 > thr;
      if (saddle && !centerIn) {
        for (let k = 0; k < 4; k++) if (cin[k]) {
          const pv = (k + 3) % 4, e_in = edge(pv), e_out = edge(k);   // edge pv is between corner pv and k ; edge k between k and k+1
          const p = [corner(k), e_out, e_in];
          tris.push(p); segs.push([e_out, e_in]);   // contour segment cuts across this corner, interior on the left
        }
        continue;
      }
      for (let k = 0; k < 4; k++) {
        if (cin[k]) { poly.push(corner(k)); isX.push(false); }
        if (cin[k] !== cin[(k + 1) % 4]) { poly.push(edge(k)); isX.push(true); }
      }
      for (let q = 1; q + 1 < poly.length; q++) tris.push([poly[0], poly[q], poly[q + 1]]);
      for (let q = 0; q < poly.length; q++) { const q2 = (q + 1) % poly.length; if (isX[q] && isX[q2]) segs.push([poly[q], poly[q2]]); }
    }
    return { vS, vT, tris, segs };
  }
  // vfn(S, T) -> [topXYZ, bottomXYZ]
  function buildSolid(m, vfn) {
    const n = m.vS.length, pos = new Float32Array(n * 6);
    for (let i = 0; i < n; i++) { const r = vfn(m.vS[i], m.vT[i]); pos.set(r[0], i * 3); pos.set(r[1], (n + i) * 3); }
    const T = [];
    for (const t of m.tris) { T.push(t[0], t[1], t[2]); T.push(n + t[0], n + t[2], n + t[1]); }
    for (const s of m.segs) { const A = s[0], B = s[1]; T.push(n + A, n + B, B); T.push(n + A, B, A); }
    let vol = 0;
    for (let i = 0; i < T.length; i += 3) {
      const a = T[i] * 3, b = T[i + 1] * 3, c = T[i + 2] * 3;
      vol += pos[a] * (pos[b + 1] * pos[c + 2] - pos[b + 2] * pos[c + 1]) - pos[a + 1] * (pos[b] * pos[c + 2] - pos[b + 2] * pos[c]) + pos[a + 2] * (pos[b] * pos[c + 1] - pos[b + 1] * pos[c]);
    }
    if (vol < 0) for (let i = 0; i < T.length; i += 3) { const t = T[i + 1]; T[i + 1] = T[i + 2]; T[i + 2] = t; }
    const out = new Float32Array(T.length * 3);
    for (let i = 0; i < T.length; i++) { out[i * 3] = pos[T[i] * 3]; out[i * 3 + 1] = pos[T[i] * 3 + 1]; out[i * 3 + 2] = pos[T[i] * 3 + 2]; }
    return out;   // 9 floats per triangle
  }
  const dot = (a, b) => a[0] * b[0] + a[1] * b[1] + a[2] * b[2];
  const sub = (a, b) => [a[0] - b[0], a[1] - b[1], a[2] - b[2]];
  const nrm = a => { const L = Math.hypot(a[0], a[1], a[2]) || 1; return [a[0] / L, a[1] / L, a[2] / L]; };
  const FLAT_MAX_RANGE = 6;        // mm the plate may curve away under one logo before it is wrapped instead of kept flat
  // Decide how to build one logo/text: a FLAT slab (flat top and bottom, tilted to the plate) or WRAPPED to the curve.
  function planFor(P, Fe, A, W, H, cell) {
    const d = P.d; let sc = 0, sS = 0, sT = 0, sLo = 1e9, sHi = -1e9; const pts = [];
    const stride = 4;
    for (let r = 0; r < H; r++) for (let c = 0; c < W; c++) {
      const k = r * W + c; if (Fe[k] > 0.5 && A[k]) {
        sc++; sS += d.S0 + c * cell; sT += r * cell; const sv = d.S0 + c * cell; if (sv < sLo) sLo = sv; if (sv > sHi) sHi = sv;
        if ((r % stride === 0) && (c % stride === 0)) pts.push(surf(P, d.S0 + c * cell, r * cell));
      }
    }
    if (sc < 8) return null;
    const Sc = sS / sc, Tc = sT / sc;
    if (pts.length < 3) pts.push(surf(P, Sc, Tc), surf(P, Sc + 1, Tc), surf(P, Sc, Tc + 1));
    const m = [[0, 0, 0], [0, 0, 0], [0, 0, 0]], v = [0, 0, 0];
    for (const p of pts) { const q = [1, p[1], p[2]]; for (let i = 0; i < 3; i++) { v[i] += q[i] * p[0]; for (let j = 0; j < 3; j++) m[i][j] += q[i] * q[j]; } }
    const det = M3 => M3[0][0] * (M3[1][1] * M3[2][2] - M3[1][2] * M3[2][1]) - M3[0][1] * (M3[1][0] * M3[2][2] - M3[1][2] * M3[2][0]) + M3[0][2] * (M3[1][0] * M3[2][1] - M3[1][1] * M3[2][0]);
    const D = det(m); let coef = [0, 0, 0];
    if (Math.abs(D) > 1e-9) { for (let i = 0; i < 3; i++) { const mm = m.map((row, r) => row.map((x, c) => c === i ? v[r] : x)); coef[i] = det(mm) / D; } } else coef = [pts[0][0], 0, 0];
    const n = nrm([-1, coef[1], coef[2]]);                       // outward (front of plate faces -X)
    const cs = surf(P, Sc, Tc);
    const C = [coef[0] + coef[1] * cs[1] + coef[2] * cs[2], cs[1], cs[2]];
    const a1 = surf(P, Sc + 1, Tc), a2 = surf(P, Sc - 1, Tc);
    let e1 = sub(a1, a2); const k1 = dot(e1, n); e1 = nrm([e1[0] - k1 * n[0], e1[1] - k1 * n[1], e1[2] - k1 * n[2]]);
    let e2 = [n[1] * e1[2] - n[2] * e1[1], n[2] * e1[0] - n[0] * e1[2], n[0] * e1[1] - n[1] * e1[0]]; if (e2[2] < 0) e2 = [-e2[0], -e2[1], -e2[2]];
    let smax = -1e9, smin = 1e9; for (const p of pts) { const s = dot(n, sub(p, C)); if (s > smax) smax = s; if (s < smin) smin = s; }
    const range = smax - smin;
    const width = sHi - sLo;
    // short logos stay flat even on the curved sides; only a very long one that runs onto the curve is wrapped
    return { Sc, Tc, n, e1, e2, C, smax, smin, range, width, flat: false };   // every logo wraps around the plate's curve (including the sides)
  }
  // FLAT logo: the top is one flat plane (true size, tilted to match the plate there); the sides drop straight down along the
  // plate's normal until they reach the plate surface (sunk H_DOWN into it), so nothing floats and nothing wraps.
  function flatVertexFn(P, pl, hUp) {
    const hTop = pl.smax + (hUp == null ? H_UP : hUp);
    return (S, T) => {
      const p = S - pl.Sc, q = T - pl.Tc;
      let s = S, t = T, B;
      for (let it = 0; it < 4; it++) { B = surf(P, s, t); const r = sub(B, pl.C); s += p - dot(r, pl.e1); t += q - dot(r, pl.e2); }
      B = surf(P, s, t); const hB = dot(pl.n, sub(B, pl.C));
      const o = [pl.C[0] + p * pl.e1[0] + q * pl.e2[0], pl.C[1] + p * pl.e1[1] + q * pl.e2[1], pl.C[2] + p * pl.e1[2] + q * pl.e2[2]];
      const lo = Math.min(hB - H_DOWN, hTop - 0.8);
      return [[o[0] + hTop * pl.n[0], o[1] + hTop * pl.n[1], o[2] + hTop * pl.n[2]], [o[0] + lo * pl.n[0], o[1] + lo * pl.n[1], o[2] + lo * pl.n[2]]];
    };
  }
  // A smoothed copy of the plate surface (the vent bars make the raw surface bumpy); logos wrap around this smooth one.
  function smoothSurface(P) {
    if (P.d.ns) return P;   // data that is already exact (no vent bars in the way): use the real surface, no smoothing
    const d = P.d, nS = d.nS, nT = d.nT, R = 2;
    const blur = A => { let src = A;
      for (let pass = 0; pass < 2; pass++) {
        const tmp = new Float32Array(A.length), out = new Float32Array(A.length);
        for (let r = 0; r < nT; r++) for (let c = 0; c < nS; c++) { let s = 0, n = 0; for (let k = -R; k <= R; k++) { const cc = c + k; if (cc >= 0 && cc < nS) { s += src[r * nS + cc]; n++; } } tmp[r * nS + c] = s / n; }
        for (let r = 0; r < nT; r++) for (let c = 0; c < nS; c++) { let s = 0, n = 0; for (let k = -R; k <= R; k++) { const rr = r + k; if (rr >= 0 && rr < nT) { s += tmp[rr * nS + c]; n++; } } out[r * nS + c] = s / n; }
        src = out;
      } return src; };
    return { d: P.d, M: P.M, X: blur(P.X), Y: blur(P.Y) };
  }
  function wrapVertexFn(P, hUp) { const Ps = smoothSurface(P), hu = hUp == null ? H_UP : hUp; return (S, T) => [pointAt(Ps, S, T, hu), pointAt(Ps, S, T, -H_DOWN)]; }
  // ---- Keep the design where the customer put it: the only things that change are (1) nothing may run up onto the sloped lip along
  // the top of the plate (it is moved down / made a little smaller so it sits just below the lip) and (2) the printable-area fit in buildDesign.
  const LIP_MARGIN = 2.5, KEEP_LAYOUT_OFF_LIP = false;   // set true to slide/shrink logos so they stay below the sloped top lip
  function lipStart(P, S) {                       // height (T) where the plate starts to slope up into the top lip at this S
    const maxT = (P.d.nT - 1) * P.d.DS; let prev = surf(P, S, 100)[0];
    for (let T = 104; T <= maxT; T += 4) { const x = surf(P, S, T)[0]; if (x - prev > 0.6) return T - 2; prev = x; }
    return maxT;
  }
  function fitToFlat(P, elems, W, H, cell) {
    if (!KEEP_LAYOUT_OFF_LIP) return 0;   // logos stay exactly where and how big the customer made them (they wrap smoothly over the lip)
    const d = P.d, S0 = d.S0; let n = 0;
    elems.forEach((Fe, idx) => {
      let cmin = W, cmax = -1, rmin = H, rmax = -1;
      for (let r = 0; r < H; r++) for (let c = 0; c < W; c++) if (Fe[r * W + c] > 0.5) { if (c < cmin) cmin = c; if (c > cmax) cmax = c; if (r < rmin) rmin = r; if (r > rmax) rmax = r; }
      if (cmax < 0) return;
      const sLo = S0 + cmin * cell, sHi = S0 + (cmax + 1) * cell, tLo = rmin * cell, tHi = (rmax + 1) * cell, h = tHi - tLo, sC = (sLo + sHi) / 2;
      let lim = 1e9; for (let S = sLo; S <= sHi + 1e-6; S += 4) lim = Math.min(lim, lipStart(P, Math.max(sLo, Math.min(sHi, S))));
      lim -= LIP_MARGIN;
      if (tHi <= lim || lim < 60) return;
      // shrink a little (never below 65%) keeping the bottom edge, then slide down the rest of the way
      const k = Math.max(0.65, Math.min(1, (lim - tLo) / h));
      const ntHi = Math.min(lim, tLo + h * k);
      const out = new Float32Array(W * H);
      for (let r = 0; r < H; r++) {
        const fr = (tHi + (r * cell - ntHi) / k) / cell; if (fr < 0 || fr > H - 1) continue;
        const r0 = Math.floor(fr), r1 = Math.min(H - 1, r0 + 1), wr = fr - r0;
        for (let c = 0; c < W; c++) {
          const fc = (sC + (S0 + c * cell - sC) / k - S0) / cell; if (fc < 0 || fc > W - 1) continue;
          const c0 = Math.floor(fc), c1 = Math.min(W - 1, c0 + 1), wc = fc - c0;
          out[r * W + c] = (Fe[r0 * W + c0] * (1 - wc) + Fe[r0 * W + c1] * wc) * (1 - wr) + (Fe[r1 * W + c0] * (1 - wc) + Fe[r1 * W + c1] * wc) * wr;
        }
      }
      elems[idx] = out; n++;
    });
    return n;
  }
  // paintAll(draw): call draw(fn) once per logo/text; fn(ctx) paints it in DEVELOPED PIXELS (1px = mDS mm; x = S, y flipped T)
  async function buildDesign(P, paintAll) {
    const d = P.d, W = d.mS, H = d.mT, cell = d.mDS;
    const tmp = document.createElement('canvas'); tmp.width = W; tmp.height = H;
    const ctx = tmp.getContext('2d', { willReadFrequently: true });
    const elems = [], fixedElems = [];
    await paintAll(async (fn, fixed) => {
      ctx.setTransform(1, 0, 0, 1, 0, 0); ctx.clearRect(0, 0, W, H); ctx.globalAlpha = 1;
      await fn(ctx);
      const px = ctx.getImageData(0, 0, W, H).data, Fe = new Float32Array(W * H);
      for (let y = 0; y < H; y++) { const r = H - 1 - y; for (let x = 0; x < W; x++) Fe[r * W + x] = px[(y * W + x) * 4 + 3] / 255; }
      (fixed ? fixedElems : elems).push(Fe);
    });
    const fitted = fitToFlat(P, elems, W, H, cell);
    const fixedSet = new Set(fixedElems);
    for (const Fe of fixedElems) elems.push(Fe);   // plate style shapes (like the line) are never moved or shrunk
    // where printing is allowed: on the plate (vent holes are bridged), 1px in from every edge, never over the Rigid Mods logo
    const A = new Uint8Array(W * H);
    for (let r = 1; r < H - 1; r++) for (let c = 1; c < W - 1; c++) {
      const k = r * W + c;
      if (P.M[k] && P.M[k - 1] && P.M[k + 1] && P.M[k - W] && P.M[k + W]) {
        const S = d.S0 + c * cell, T = r * cell;
        if (!(Math.abs(S) < 27 && T < 24)) A[k] = 1;
      }
    }
    // Resize a little so everything sits fully on the printable area (a small margin in from every edge, the vents and the logo zone):
    // anything that would be cut off is scaled down around its own centre until it fits.
    {
      const E_ = Math.round(1.5 / cell), Am = new Uint8Array(A);          // 3 cells = 1.5 mm margin
      for (let it = 0; it < E_; it++) { const prev = new Uint8Array(Am); for (let r = 1; r < H - 1; r++) for (let c = 1; c < W - 1; c++) { const k = r * W + c; if (prev[k] && !(prev[k - 1] && prev[k + 1] && prev[k - W] && prev[k + W])) Am[k] = 0; } }
      const outside = Fe => { let t = 0, o = 0; for (let k = 0; k < Fe.length; k++) if (Fe[k] > 0.5) { t++; if (!Am[k]) o++; } return t ? o / t : 0; };
      for (let i = 0; i < elems.length - fixedElems.length; i++) {
        let Fe = elems[i]; if (outside(Fe) < 0.003) continue;
        {   // first try sliding it a little (up to 5 mm) to a spot where it fits; only what still hangs over after that is made smaller
          const idx = []; for (let k = 0; k < Fe.length; k++) if (Fe[k] > 0.5) idx.push(k);
          let bo = idx.length + 1, bx = 0, by = 0;
          const RS = Math.round(5 / cell), SD = Math.max(1, Math.round(0.5 / cell));
          for (let dy = -RS; dy <= RS; dy += SD) for (let dx = -RS; dx <= RS; dx += SD) {
            let o = 0; for (let q = 0; q < idx.length; q++) { const k = idx[q], r = ((k / W) | 0) + dy, c = (k % W) + dx; if (r < 0 || r >= H || c < 0 || c >= W || !Am[r * W + c]) o++; }
            if (o < bo || (o === bo && Math.abs(dx) + Math.abs(dy) < Math.abs(bx) + Math.abs(by))) { bo = o; bx = dx; by = dy; }
          }
          if (bx || by) {
            const moved = new Float32Array(W * H);
            for (let q = 0; q < idx.length; q++) { const k = idx[q], r = ((k / W) | 0) + by, c = (k % W) + bx; if (r >= 0 && r < H && c >= 0 && c < W) moved[r * W + c] = Fe[k]; }
            Fe = moved; elems[i] = moved;
            if (outside(Fe) < 0.003) continue;
          }
        }
        let sr = 0, sc = 0, n = 0; for (let r = 0; r < H; r++) for (let c = 0; c < W; c++) if (Fe[r * W + c] > 0.5) { sr += r; sc += c; n++; }
        if (!n) continue; const cr = sr / n, cc = sc / n; let best = null;
        for (let k = 0.97; k >= 0.45; k -= 0.03) {
          const out = new Float32Array(W * H);
          for (let r = 0; r < H; r++) { const fr = cr + (r - cr) / k; if (fr < 0 || fr > H - 1) continue; const r0 = Math.floor(fr), r1 = Math.min(H - 1, r0 + 1), wr = fr - r0;
            for (let c = 0; c < W; c++) { const fc = cc + (c - cc) / k; if (fc < 0 || fc > W - 1) continue; const c0 = Math.floor(fc), c1 = Math.min(W - 1, c0 + 1), wc = fc - c0;
              out[r * W + c] = (Fe[r0 * W + c0] * (1 - wc) + Fe[r0 * W + c1] * wc) * (1 - wr) + (Fe[r1 * W + c0] * (1 - wc) + Fe[r1 * W + c1] * wc) * wr; } }
          best = out; if (outside(out) < 0.003) break;
        }
        if (best) elems[i] = best;
      }
    }
    let before = 0, after = 0, totalCells = 0;
    for (const Fe of elems) for (let k = 0; k < Fe.length; k++) { before += Fe[k]; if (A[k]) { after += Fe[k]; if (Fe[k] > 0.5) totalCells++; } else Fe[k] = 0; }
    if (totalCells < 8) return { tris: new Float32Array(0), stats: { empty: true, trimmed: before > 0 ? 1 : 0 } };
    const step = totalCells > 28000 ? Math.ceil(Math.sqrt(totalCells / 28000)) : 1;
    const parts = [], partIdx = []; let nFlat = 0, nWrap = 0; const ranges = [];
    for (const Fe of elems) {
      const ei = elems.indexOf(Fe);
      const hUp = fixedSet.has(Fe) ? H_UP_STYLE : H_UP;
      const pl = planFor(P, Fe, A, W, H, cell); if (!pl) continue; ranges.push([+pl.Sc.toFixed(0), +pl.Tc.toFixed(0), +pl.range.toFixed(1), +pl.width.toFixed(0)]);
      let own = 0; for (let k = 0; k < Fe.length; k++) if (Fe[k] > 0.5) own++;
      const stepE = own > 60000 ? Math.ceil(Math.sqrt(own / 60000)) : 1;   // every piece picks its own detail level (a big style shape no longer coarsens the images)
      let nx = W, ny = H, G = Fe, gcell = cell;
      if (stepE > 1) { nx = Math.ceil(W / stepE); ny = Math.ceil(H / stepE); gcell = cell * stepE; G = new Float32Array(nx * ny); for (let r = 0; r < ny; r++) for (let c = 0; c < nx; c++) { let sum = 0, cnt = 0; for (let dy = 0; dy < stepE; dy++) for (let dx = 0; dx < stepE; dx++) { const rr = Math.min(H - 1, r * stepE + dy - (stepE >> 1)), cc = Math.min(W - 1, c * stepE + dx - (stepE >> 1)); if (rr >= 0 && cc >= 0) { sum += Fe[rr * W + cc]; cnt++; } } G[r * nx + c] = cnt ? sum / cnt : 0; } }
      for (let c = 0; c < nx; c++) { G[c] = 0; G[(ny - 1) * nx + c] = 0; } for (let r = 0; r < ny; r++) { G[r * nx] = 0; G[r * nx + nx - 1] = 0; }
      const m = meshFromField(P, G, nx, ny, gcell, d.S0, 0.5);
      if (!m.tris.length) continue;
      if (pl.flat) { nFlat++; parts.push(buildSolid(m, flatVertexFn(P, pl, hUp))); } else { nWrap++; parts.push(buildSolid(m, wrapVertexFn(P, hUp))); }
      partIdx.push(ei);
    }
    let len = 0; for (const p of parts) len += p.length; const tris = new Float32Array(len); let o = 0; for (const p of parts) { tris.set(p, o); o += p.length; }
    return { tris, parts, partIdx, stats: { fitted: fitted, cells: totalCells, tris: tris.length / 9, step, flat: nFlat, wrapped: nWrap, ranges: ranges, trimmed: before > 0 ? 1 - after / before : 0 } };
  }
  function parseBinarySTL(buf) {
    const dv = new DataView(buf); const n = dv.getUint32(80, true);
    if (84 + n * 50 !== buf.byteLength) return null;
    const out = new Float32Array(n * 9);
    for (let i = 0; i < n; i++) { const o = 84 + i * 50 + 12; for (let k = 0; k < 9; k++) out[i * 9 + k] = dv.getFloat32(o + k * 4, true); }
    return out;
  }
  function writeBinarySTL(parts, header) {
    let n = 0; for (const p of parts) n += p.length / 9;
    const buf = new ArrayBuffer(84 + n * 50), dv = new DataView(buf);
    const h = (header || 'Rigid Mods custom plate').slice(0, 79); for (let i = 0; i < h.length; i++) dv.setUint8(i, h.charCodeAt(i) & 127);
    dv.setUint32(80, n, true); let o = 84;
    for (const p of parts) for (let i = 0; i < p.length; i += 9) {
      const ux = p[i + 3] - p[i], uy = p[i + 4] - p[i + 1], uz = p[i + 5] - p[i + 2], vx = p[i + 6] - p[i], vy = p[i + 7] - p[i + 1], vz = p[i + 8] - p[i + 2];
      let nx = uy * vz - uz * vy, ny = uz * vx - ux * vz, nz = ux * vy - uy * vx; const L = Math.hypot(nx, ny, nz) || 1;
      dv.setFloat32(o, nx / L, true); dv.setFloat32(o + 4, ny / L, true); dv.setFloat32(o + 8, nz / L, true);
      for (let k = 0; k < 9; k++) dv.setFloat32(o + 12 + k * 4, p[i + k], true);
      dv.setUint16(o + 48, 0, true); o += 50;
    }
    return buf;
  }
  return { decode, refine, surf, pointAt, meshFromField, buildSolid, buildDesign, fitToFlat, planFor, parseBinarySTL, writeBinarySTL, H_UP, H_DOWN };
})();

window.PSTL = PSTL;
